import p5 from 'p5';
import { SiriusGenerator } from './generators/sirius.js';
import { WaltzGenerator } from './generators/waltz.js';
import { IrohaniGenerator } from './generators/irohani.js';
import { DioramaGenerator } from './generators/diorama.js';
import { PandoraGenerator } from './generators/pandora.js';
import { HydrangeaGenerator } from './generators/hydrangea.js';
import { HousekiGenerator } from './generators/houseki.js';
import { DimensionGenerator } from './generators/dimension.js';

export class AtelierSketch {
  constructor(containerElement, onInitCallback) {
    this.container = containerElement;
    this.onInitCallback = onInitCallback;
    this.p5Instance = null;
    this.currentSongId = 'sirius';
    this.params = {
      motion: 0.5,
      density: 0.5,
      glow: 0.6
    };
    this.generators = {};
    this.canvas = null;

    this.initP5();
  }

  calcCanvasSize() {
    const isMobile = window.innerWidth <= 960;
    const isFullscreen = document.body.classList.contains('fullscreen-active');

    if (isFullscreen) {
      return {
        width: window.innerWidth,
        height: window.innerHeight
      };
    }

    const container = this.container;
    const containerWidth = container ? container.clientWidth : 0;
    let w = containerWidth || (isMobile ? Math.min(window.innerWidth - 32, 600) : 800);
    w = Math.max(280, Math.min(w, 1200));

    let h;
    if (isMobile) {
      // スマホレイアウトは正方形 (1:1)
      h = w;
    } else {
      // デスクトップは長方形 (4:3)
      h = Math.round(w * 0.75);
      const maxH = Math.floor(window.innerHeight * 0.75);
      if (h > maxH && maxH > 300) {
        h = maxH;
        w = Math.round((h * 4) / 3);
      }
    }

    return {
      width: Math.floor(w),
      height: Math.floor(h)
    };
  }

  initP5() {
    const sketch = (p) => {
      p.setup = () => {
        const size = this.calcCanvasSize();
        const renderer = p.createCanvas(size.width, size.height);
        this.canvas = renderer.canvas;

        // p5.jsのキャンバス要素に明示的な表示サイズを設定
        renderer.style('width', `${size.width}px`);
        renderer.style('height', `${size.height}px`);

        // 高DPIディスプレイ対応
        p.pixelDensity(Math.min(window.devicePixelRatio || 2, 2));

        // ジェネレーター群の初期化
        this.generators = {
          sirius: new SiriusGenerator(p),
          waltz: new WaltzGenerator(p),
          irohani: new IrohaniGenerator(p),
          hydrangea: new HydrangeaGenerator(p),
          diorama: new DioramaGenerator(p),
          houseki: new HousekiGenerator(p),
          dimension: new DimensionGenerator(p),
          pandora: new PandoraGenerator(p)
        };

        this.getActiveGenerator().init(this.params);

        if (this.onInitCallback) {
          this.onInitCallback(this.canvas);
        }
      };

      p.draw = () => {
        const active = this.getActiveGenerator();
        if (active) {
          active.draw(this.params);
        }
      };

      p.windowResized = () => {
        const size = this.calcCanvasSize();
        if (size.width > 200 && (p.width !== size.width || p.height !== size.height)) {
          p.resizeCanvas(size.width, size.height);
          if (this.canvas) {
            this.canvas.style.width = `${size.width}px`;
            this.canvas.style.height = `${size.height}px`;
          }
          this.getActiveGenerator().init(this.params);
        }
      };

      p.mousePressed = (e) => {
        if (p.mouseX >= 0 && p.mouseX <= p.width && p.mouseY >= 0 && p.mouseY <= p.height) {
          const active = this.getActiveGenerator();
          if (active && active.onPointerDown) {
            active.onPointerDown(p.mouseX, p.mouseY);
          }
        }
      };

      p.mouseMoved = () => {
        if (p.mouseX >= 0 && p.mouseX <= p.width && p.mouseY >= 0 && p.mouseY <= p.height) {
          const active = this.getActiveGenerator();
          if (active && active.onPointerMove) {
            active.onPointerMove(p.mouseX, p.mouseY);
          }
        }
      };

      p.mouseDragged = () => {
        if (p.mouseX >= 0 && p.mouseX <= p.width && p.mouseY >= 0 && p.mouseY <= p.height) {
          const active = this.getActiveGenerator();
          if (active && active.onPointerMove) {
            active.onPointerMove(p.mouseX, p.mouseY);
          }
        }
      };

      p.touchStarted = (e) => {
        if (p.touches.length > 0) {
          const touch = p.touches[0];
          const rect = this.canvas.getBoundingClientRect();
          const tx = touch.x - rect.left;
          const ty = touch.y - rect.top;
          if (tx >= 0 && tx <= p.width && ty >= 0 && ty <= p.height) {
            const active = this.getActiveGenerator();
            if (active && active.onPointerDown) {
              active.onPointerDown(tx, ty);
            }
          }
        }
      };

      p.touchMoved = (e) => {
        if (p.touches.length > 0) {
          const touch = p.touches[0];
          const rect = this.canvas.getBoundingClientRect();
          const tx = touch.x - rect.left;
          const ty = touch.y - rect.top;
          if (tx >= 0 && tx <= p.width && ty >= 0 && ty <= p.height) {
            const active = this.getActiveGenerator();
            if (active && active.onPointerMove) {
              active.onPointerMove(tx, ty);
            }
          }
        }
      };
    };

    this.p5Instance = new p5(sketch, this.container);
  }

  getActiveGenerator() {
    return this.generators[this.currentSongId];
  }

  setSong(songId) {
    if (this.generators[songId]) {
      this.currentSongId = songId;
      this.getActiveGenerator().init(this.params);
    }
  }

  setParam(key, value) {
    this.params[key] = value;
    if (key === 'density') {
      // 密度が変わったら要素数を再計算
      this.getActiveGenerator().init(this.params);
    }
  }

  randomizeParams() {
    this.params.motion = Math.round(Math.random() * 80 + 20) / 100;
    this.params.density = Math.round(Math.random() * 80 + 20) / 100;
    this.params.glow = Math.round(Math.random() * 70 + 30) / 100;
    const active = this.getActiveGenerator();
    if (active && active.onReroll) {
      active.onReroll();
    }
    if (active) {
      active.init(this.params);
    }
    return { ...this.params };
  }

  saveImage(filename = 'isekai-joucho-art.png') {
    if (this.p5Instance) {
      this.p5Instance.saveCanvas(filename, 'png');
    }
  }

  togglePlay() {
    if (!this.p5Instance) return true;
    if (this.p5Instance.isLooping()) {
      this.p5Instance.noLoop();
      return false; // 現在は一時停止
    } else {
      this.p5Instance.loop();
      return true; // 現在は再生中
    }
  }

  isPlaying() {
    return this.p5Instance ? this.p5Instance.isLooping() : true;
  }

  getCanvasElement() {
    return this.canvas;
  }
}
